import React from 'react';
import {
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  Box,
  Checkbox,
  ListItemText,
  Avatar
} from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import BusinessIcon from '@material-ui/icons/Business';
import AllInclusiveIcon from '@material-ui/icons/AllInclusive';

const useStyles = makeStyles((theme) => ({
  formControl: {
    minWidth: 200,
    maxWidth: 400,
    backgroundColor: '#fff',
    borderRadius: theme.spacing(0.5),
    '& .MuiOutlinedInput-root': {
      '&:hover fieldset': {
        borderColor: theme.palette.primary.main
      }
    }
  },
  selectInput: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1)
  },
  chips: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: theme.spacing(0.5)
  },
  chip: {
    backgroundColor: theme.palette.primary.light,
    color: theme.palette.primary.dark,
    fontWeight: 500,
    '& .MuiChip-avatar': {
      width: 20,
      height: 20,
      '& img': {
        width: '100%',
        height: '100%',
        objectFit: 'contain'
      }
    }
  },
  allChip: {
    backgroundColor: theme.palette.secondary.main,
    color: '#fff'
  },
  menuItem: {
    '&.Mui-selected': {
      backgroundColor: `${theme.palette.primary.light}20`,
      '&:hover': {
        backgroundColor: `${theme.palette.primary.light}30`
      }
    }
  },
  icon: {
    marginRight: theme.spacing(1),
    color: theme.palette.primary.main
  }
}));

const EntitySelector = ({ entities = [], selectedEntities = ['all'], onChange }) => {
  const classes = useStyles();

  const handleChange = (event) => {
    const value = event.target.value;
    
    // Handle "all" selection
    if (value.includes('all')) {
      if (selectedEntities.includes('all')) {
        // If "all" was already selected and user clicks something else, remove "all"
        onChange(value.filter(v => v !== 'all'));
      } else {
        // If user selects "all", clear other selections
        onChange(['all']);
      }
    } else {
      onChange(value);
    }
  };

  const renderValue = (selected) => {
    if (selected.includes('all')) {
      return (
        <Box className={classes.chips}>
          <Chip
            size="small"
            label="All Entities"
            className={`${classes.chip} ${classes.allChip}`}
            icon={<AllInclusiveIcon />}
          />
        </Box>
      );
    }

    return (
      <Box className={classes.chips}>
        {selected.map((value) => {
          const entity = entities.find(e => e._id === value);
          return (
            <Chip
              key={value}
              size="small"
              label={entity?.internalName || entity?.name || entity?.entityName || value}
              className={classes.chip}
              avatar={
                <Avatar 
                  src={`${process.env.PUBLIC_URL}/${entity._id}_Logo.png`}
                  style={{ width: 20, height: 20 }}
                >
                  {(entity?.internalName || entity?.name || 'E').charAt(0).toUpperCase()}
                </Avatar>
              }
            />
          );
        })}
      </Box>
    );
  };

  const isAllSelected = selectedEntities.includes('all');

  return (
    <FormControl 
      variant="outlined" 
      size="small"
      className={classes.formControl}
    >
      <InputLabel id="entity-selector-label">
        <Box className={classes.selectInput}>
          <BusinessIcon fontSize="small" />
          Select Entities
        </Box>
      </InputLabel>
      <Select
        labelId="entity-selector-label"
        id="entity-selector"
        multiple
        value={selectedEntities}
        onChange={handleChange}
        renderValue={renderValue}
        label="Select Entities"
      >
        <MenuItem value="all" className={classes.menuItem}>
          <Checkbox checked={isAllSelected} color="primary" />
          <ListItemText primary="All Entities" />
        </MenuItem>
        {entities.map((entity) => (
          <MenuItem 
            key={entity._id} 
            value={entity._id}
            className={classes.menuItem}
            disabled={isAllSelected}
          >
            <Checkbox 
              checked={selectedEntities.includes(entity._id)} 
              color="primary"
              disabled={isAllSelected}
            />
            <img 
              src={`${process.env.PUBLIC_URL}/${entity._id}_Logo.png`}
              alt={entity.internalName || entity.name || 'Entity'}
              style={{ 
                width: 24, 
                height: 24, 
                marginRight: 8,
                objectFit: 'contain'
              }}
              onError={(e) => {
                // Fallback to avatar with letter if image fails
                e.target.style.display = 'none';
                e.target.nextSibling.style.display = 'flex';
              }}
            />
            <Avatar 
              style={{ 
                width: 24, 
                height: 24, 
                marginRight: 8,
                backgroundColor: '#1976d2',
                fontSize: '0.875rem',
                display: 'none'
              }}
            >
              {(entity.internalName || entity.name || 'E').charAt(0).toUpperCase()}
            </Avatar>
            <ListItemText primary={entity.internalName || entity.name || entity.entityName || 'Unnamed Entity'} />
          </MenuItem>
        ))}
      </Select>
    </FormControl>
  );
};

export default EntitySelector;